import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Heart, Star, Sparkles, Crown, Check, ArrowUpRight,
  Zap, TrendingUp, Shield, Video, Loader2,
} from 'lucide-react';
import { getPlanMeta, ADDON_META } from '@/lib/plans';

const PLAN_ICONS = { Heart, Star, Sparkles, Crown };
const ADDON_ICONS = { Zap, TrendingUp, Heart, Shield, Video };

export const PlanStatusCard = ({ subscription, onCancel }) => {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const planType = subscription?.plan_type || 'free';
  const meta = getPlanMeta(planType);
  const PlanIcon = PLAN_ICONS[meta.icon] || Heart;
  const activeAddons = subscription?.active_addons || [];
  const isFree = planType === 'free';

  const handleCancel = async () => {
    if (!onCancel) return;
    setCancelling(true);
    try { await onCancel(); } finally { setCancelling(false); }
  };

  return (
    <Card data-testid="plan-status-card" className="shadow-xl border-rose-100 overflow-hidden">
      <div className={`px-6 py-5 ${meta.pillClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/25 flex items-center justify-center">
              <PlanIcon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide opacity-80">Current Plan</p>
              <p className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {meta.label}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium opacity-90">{meta.matchesLabel}</p>
            {subscription?.status && (
              <p className="text-xs opacity-75 capitalize">{subscription.status}</p>
            )}
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-5">
        <div>
          <p className="text-sm font-semibold text-rose-900 mb-3">What's included</p>
          <ul className="grid sm:grid-cols-2 gap-2">
            {meta.perks.map((perk) => (
              <li key={perk} className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-rose-500 flex-shrink-0" />
                {perk}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-sm font-semibold text-rose-900 mb-3">Your add-ons</p>
          {activeAddons.length === 0 ? (
            <p data-testid="no-addons-text" className="text-sm text-gray-500">
              No add-ons yet. Boost your experience from the pricing page.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="active-addons-list">
              {activeAddons.map((addon) => {
                const am = ADDON_META[addon] || { label: addon, icon: 'Zap', colorClass: 'text-rose-500' };
                const AddonIcon = ADDON_ICONS[am.icon] || Zap;
                return (
                  <span
                    key={addon}
                    data-testid={`addon-chip-${addon}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-full text-sm text-rose-800"
                  >
                    <AddonIcon className={`w-4 h-4 ${am.colorClass}`} />
                    {am.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <Button
          data-testid="manage-plan-btn"
          variant={isFree ? 'default' : 'outline'}
          onClick={() => navigate('/pricing')}
          className={isFree
            ? 'w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white'
            : 'w-full border-rose-200 text-rose-700 hover:bg-rose-50'}
        >
          {isFree ? 'Upgrade Your Plan' : 'Manage Plan & Add-ons'}
          <ArrowUpRight className="w-4 h-4 ml-2" />
        </Button>

        {!isFree && subscription?.status !== 'canceled' && onCancel && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                data-testid="cancel-subscription-btn"
                variant="ghost"
                disabled={cancelling}
                className="w-full text-gray-500 hover:text-red-600 hover:bg-red-50"
              >
                {cancelling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling…</> : 'Cancel subscription'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="cancel-subscription-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel your {meta.label} plan?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your subscription won't renew and you'll move to the Free plan. You can re-subscribe anytime.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-subscription-dismiss">Keep my plan</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="cancel-subscription-confirm"
                  onClick={handleCancel}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Yes, cancel
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  );
};

export default PlanStatusCard;
